import React, { useState, useEffect, useRef, } from 'react';
import { useParams } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import parse from 'html-react-parser';

import useAuth from '../../auth/UseAuth';

import {
    getResponse, settings, styles, handleResponseMessageAsync, isUndNullEmp, getLanguageObj, notificationsToastGetAsync, notificationsToastShowAsync,
    LoadingSection,
} from '../../helpers/main';
import Menu from '../main/Menu';

import { GlobalFormats } from '../../classess/main/globalFormats';
import { Toast, } from '../../classess/notifications/toast.js'
import { NotificationToast, } from '../../classess/main/notifications/notificationToast.js'
import { User } from '../../classess/user/user';
import { PasswordRecoverService } from '../../services/auth/PasswordRecoverService';

const { app, backend, icons, images, labels, messages, } = settings;

const currentMethod = 'PasswordRecoverChangePassword';

export default function PasswordRecoverChangePassword({
    languageObj = GlobalFormats?.getClass(getLanguageObj({ subMethod: currentMethod })?.result),
}) {
    //#region variables
    const auth = useAuth();
    const { id, } = useParams();
    const [showData, setShowData] = useState(true);
    const [process, setProcess] = useState({ showLoading: true, send: false });

    const [dataForm, setDataForm] = useState({ id: id, email: '', firstName: '', lastName: '', password: '', passwordConfirm: '' });
    const [dataFormLength] = useState(User?.minMaxLength(false));
    const [errors, setErrors] = useState({ password: '', passwordConfirm: '' });
    const [showPassword, setShowPassword] = useState(false);

    const isMount = useRef(true);
    const reCaptchaRef = useRef(ReCAPTCHA);

    const controls = {
        buttons: {
            back: {
                defaultClass: 'btn btn-outline-secondary shadow-sm font-weight-bold mx-1 px-4',
                id: 'btnBack',
            },
            home: {
                defaultClass: 'btn btn-light border shadow-sm font-weight-bold text-secondary mx-1 px-4',
                id: 'btnHome',
            },
            send: {
                defaultClass: 'btn btn-primary shadow-sm font-weight-bold mx-1 px-4',
                id: 'btnSend',
            },
        },
        icons: {
            send: {
                id: 'iconSend',
                defaultClass: icons?.powerOff,
            },
        },
        inputs: {
            email: {
                id: 'inputEmail',
            },
            firstName: {
                id: 'inputFirstName',
            },
            lastName: {
                id: 'inputLastName',
            },
            password: {
                id: 'inputPassword',
            },
            passwordConfirm: {
                id: 'inputPasswordConfirm',
            },
        },
        spinners: {
            send: {
                id: 'spSend',
                defaultClass: '',
            }
        },
    };
    //#endregion variables

    //#region methods
    const setErrorsDefault = ({
        subMethod = '',
    } = {}) => {
        const currentMethod = 'setErrorsDefault';
        let response = getResponse({ type: 0 });
        try {
            setErrors(prev => ({ ...prev, password: '', passwordConfirm: '' }));
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    const validateFormData = ({
        subMethod = '',
    } = {}) => {
        const currentMethod = 'validateFormData';
        let response = getResponse({ type: 0 });
        try {
            let passwordError = '';
            let passwordConfirmError = '';

            if (isUndNullEmp({ data: dataForm, subMethod: currentMethod })?.result) {
                passwordError = messages?.thisFieldRequired?.[languageObj?.id];
                passwordConfirmError = messages?.thisFieldRequired?.[languageObj?.id];
                response.isWarning = true;
            }

            if (isUndNullEmp({ data: dataForm?.password, subMethod: currentMethod })?.result) {
                passwordError = messages?.thisFieldRequired?.[languageObj?.id];
                response.isWarning = true;
            } else if (dataForm?.password?.length < settings?.passwords?.minLength) {
                passwordError = messages?.youMustEnterValidNewPassword[languageObj?.id];
                response.isWarning = true;
            }

            if (isUndNullEmp({ data: dataForm?.passwordConfirm, subMethod: currentMethod })?.result) {
                passwordConfirmError = messages?.thisFieldRequired?.[languageObj?.id];
                response.isWarning = true;
            } else if (dataForm?.passwordConfirm?.length < settings?.passwords?.minLength) {
                passwordConfirmError = messages?.youMustEnterValidNewPasswordConfirm[languageObj?.id];
                response.isWarning = true;
            }

            if (!response.isWarning && dataForm?.password !== dataForm?.passwordConfirm) {
                passwordError = messages?.newPasswordAndNewPasswordConfirmNotMatch[languageObj?.id];
                passwordConfirmError = messages?.newPasswordAndNewPasswordConfirmNotMatch[languageObj?.id];
                response.isWarning = true;
            }

            setErrors(prev => ({ ...prev, password: passwordError, passwordConfirm: passwordConfirmError }));
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        }
        return response;
    }

    const changePassword = async ({
        e = undefined,
        subMethod = '',
    } = {}) => {
        const currentMethod = 'changePassword';
        let response = getResponse({ type: 0 });
        try {
            if (e) e?.preventDefault?.();

            //#region error default / validate data
            response = setErrorsDefault({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;

            response = validateFormData({ subMethod: currentMethod });
            if (!response?.isSuccess || response?.isWarning) return;
            //#endregion error default / validate data

            setProcess(prev => ({ ...prev, send: true, showLoading: true }));
            setShowPassword(false);

            //#region change password
            setShowPassword(false);

            const reCaptchaToken = await reCaptchaRef?.current?.executeAsync();
            reCaptchaRef?.current?.reset();

            response = await PasswordRecoverService.changePasswordAsync({
                id: dataForm?.id,
                password: dataForm?.password,
                passwordConfirm: dataForm?.passwordConfirm,
                reCaptchaToken: reCaptchaToken,
                languageId: languageObj?.id,
                subMethod: currentMethod,
            });

            if (!response?.isSuccess || response?.isWarning) return;

            setDataForm(prev => ({
                ...prev,
                password: '',
                passwordConfirm: '',
            }));
            //#endregion change password
        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod, })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, send: false, showLoading: false }));

            // CSN
            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({
                    response,
                    currentMethod,
                    auth,
                    languageId: languageObj?.id,
                    setShowData,
                    showDataValue: response?.isSuccess
                });
            }
        }
    }

    const getData = async ({
        subMethod = '',
    } = {}) => {
        const currentMethod = 'getData';
        let response = getResponse({ type: 0 });
        try {
            setProcess(prev => ({ ...prev, showLoading: true }));

            //#region variables
            let notificationToastsData = [Toast?.getClass({}, false)];
            let notificationsToastObj = {
                ...NotificationToast?.getClass({}, false),
                controller: app?.views?.accounts?.accounts,
                method: app?.views?.accounts?.passwordRecover,
                isAuthenticated: false,
            };
            //#endregion variables

            //#region notificationtoast (get)
            response = await notificationsToastGetAsync({
                data: notificationsToastObj,
                languageId: languageObj?.id,
                subMethod: currentMethod,
            });

            if (!response?.isSuccess || response?.isWarning) return;

            notificationToastsData = response?.result?.toasts || []
            //#endregion notificationtoast (get)

            //#region change password (get data)
            response = await PasswordRecoverService.passwordRecoverCheckAsync({
                id: id,
                languageId: languageObj?.id,
                subMethod: currentMethod,
            });

            if (!response?.isSuccess || response?.isWarning) return;

            setDataForm(prev => ({
                ...prev,
                email: response?.result?.user?.email,
                firstName: response?.result?.user?.firstName,
                lastName: response?.result?.user?.lastName,
                password: '',
                passwordConfirm: '',
            }));
            //#endregion change password (get data)

            //#region show toasts
            response = await notificationsToastShowAsync({
                data: notificationToastsData,
                languageId: languageObj?.id,
                window: window,
                document: document,
                subMethod: currentMethod,
            });

            if (!response?.isSuccess || response?.isWarning) return;
            //#endregion show toasts

        } catch (ex) {
            response = getResponse({ type: -1 });
            response.msgText = `Method: ${currentMethod}<br/>`;
            if (!isUndNullEmp({ data: subMethod, subMethod: currentMethod, })?.result) {
                response.msgText += `Sub method: ${subMethod}<br/>`;
            }
            response.msgText += `Error: ${ex?.message}<br/>`;
        } finally {
            if (!isMount?.current) return;

            setProcess(prev => ({ ...prev, showLoading: false }));

            if (!response?.isSuccess || response?.isWarning) {
                setShowData(false);
                response.urlGoTo = response.urlGoTo || backend?.urls?.login;
            }

            // CSN
            if (!response?.isSuccess || response?.isWarning || response?.msgText !== '' || response?.msgTextHtml !== '') {
                await handleResponseMessageAsync({
                    response,
                    currentMethod,
                    auth,
                    languageId: languageObj?.id,
                    // setShowData,
                    // showDataValue: (response?.isSuccess && !response?.isWarning),
                });
            }
        }
    }

    useEffect(() => {
        isMount.current = true;
        getData({ subMethod: 'form - onLoad' });
        return () => {
            isMount.current = false;
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    //#endregion methods

    return (
        <div>
            <Menu viewName={app?.views?.main?.home} />

            <div className='content-wrapper bg-transparent'>

                {/* Content Header (Page header) */}
                <div className='content-header'>
                    <div className='container-fluid'>
                        <div className='row mb-2 align-items-center bg-white p-3 rounded shadow-lg mx-0'>
                            <div className='col-sm-6'>
                                <h4 className='m-0 font-weight-bold text-dark'>{labels?.changePassword?.[languageObj?.id]}</h4>
                            </div>
                            <div className='col-sm-6'>
                                <ol className='breadcrumb float-sm-right bg-transparent mb-0'>
                                    <li className='breadcrumb-item'><a href='/'>{labels?.home?.[languageObj?.id]}</a></li>
                                    <li className='breadcrumb-item active text-primary'>{labels?.changePassword?.[languageObj?.id]}</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
                {/* /.content-header */}

                {/* Main content */}
                <section className='content'>
                    <div className='container-fluid'>
                        {showData ? (
                            <div className='row'>
                                <div className='col-12'>
                                    <div className='login-box' style={styles?.loginBox}>

                                        <div className={settings?.html?.cards?.default} style={{ borderRadius: '10px' }}>
                                            <LoadingSection alt='loadingSection'
                                                className='overlay dark text-white'
                                                height={20}
                                                hidden={!process?.showLoading}
                                                width={20}
                                                languageId={languageObj?.id}
                                                subMethod={currentMethod} />

                                            {/*Header*/}
                                            <div className='card-header border-bottom-0 pt-4 px-4 bg-white'>
                                                <div className='d-flex align-items-center w-100'>
                                                    <div className='bg-light p-2 rounded-circle mr-3 shadow-sm'
                                                        style={{ width: '45px', height: '45px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                        <img src={images?.icons?.password} alt='UserLogin' style={styles?.cardHeaderLogo} />
                                                    </div>
                                                    <div className='d-flex flex-column'>
                                                        <h5 className='mb-0 font-weight-bold text-dark'>{labels?.changePassword?.[languageObj?.id]}</h5>
                                                        <div className='mt-1'>
                                                            <span className='badge badge-pill badge-info px-2 py-1 text-uppercase' style={settings?.html?.badges?.micro}>
                                                                <i className={`${icons?.fasFa?.idBadge} mr-1`}></i> {labels?.personalInformation?.[languageObj?.id]}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            <form onSubmit={(e) => changePassword({
                                                e: e,
                                                subMethod: 'changePasswordForm'
                                            })} method='post'>

                                                {/*Body*/}
                                                <div className='card-body px-4 pt-2'>

                                                    <div className='form-group mb-4'>
                                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                                            <i className={`${icons?.envelope} mr-1`} /> {labels?.email?.[languageObj?.id]}
                                                        </small>
                                                        <input id={controls?.inputs?.email?.id}
                                                            className='form-control form-control-lg bg-light border-0 shadow-sm text-dark'
                                                            style={{ borderRadius: '5px' }}
                                                            maxLength={dataFormLength?.max?.email}
                                                            onChange={(e) => setDataForm(prev => ({ ...prev, email: e?.target?.value }))}
                                                            value={dataForm?.email || ''}
                                                            placeholder={labels?.email?.[languageObj?.id]}
                                                            readOnly={true}
                                                            title={labels?.email?.[languageObj?.id]} />
                                                    </div>
                                                    <div className='form-group mb-4'>
                                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                                            <i className={`${icons?.fasFa?.user} mr-1`} /> {labels?.firstName?.[languageObj?.id]}
                                                        </small>
                                                        <input id={controls?.inputs?.firstName?.id}
                                                            className='form-control form-control-lg bg-light border-0 shadow-sm text-dark'
                                                            style={{ borderRadius: '5px' }}
                                                            maxLength={dataFormLength?.max?.firstName}
                                                            onChange={(e) => setDataForm(prev => ({ ...prev, firstName: e?.target?.value }))}
                                                            value={dataForm?.firstName || ''}
                                                            placeholder={labels?.firstName?.[languageObj?.id]}
                                                            readOnly={true}
                                                            title={labels?.firstName?.[languageObj?.id]} />
                                                    </div>
                                                    <div className='form-group mb-4'>
                                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                                            <i className={`${icons?.fasFa?.user} mr-1`} /> {labels?.lastName?.[languageObj?.id]}
                                                        </small>
                                                        <input id={controls?.inputs?.lastName?.id}
                                                            className='form-control form-control-lg bg-light border-0 shadow-sm text-dark'
                                                            style={{ borderRadius: '5px' }}
                                                            maxLength={dataFormLength?.max?.lastName}
                                                            onChange={(e) => setDataForm(prev => ({ ...prev, lastName: e?.target?.value }))}
                                                            value={dataForm?.lastName || ''}
                                                            placeholder={labels?.lastName?.[languageObj?.id]}
                                                            readOnly={true}
                                                            title={labels?.lastName?.[languageObj?.id]} />
                                                    </div>
                                                    <div className='form-group mb-4'>
                                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                                            <i className={`${icons?.fasFa?.lock} mr-1`} /> {labels?.newPassword?.[languageObj?.id]}
                                                        </small>
                                                        <div className='input-group shadow-sm' style={{ borderRadius: '5px', overflow: 'hidden' }}>
                                                            <input autoFocus={true}
                                                                id={controls?.inputs?.password?.id}
                                                                className='form-control form-control-lg bg-light border-0 text-dark'
                                                                maxLength={dataFormLength?.max?.password}
                                                                onChange={(e) => setDataForm(prev => ({ ...prev, password: e?.target?.value }))}
                                                                onFocus={() => setErrors(prev => ({ ...prev, password: '' }))}
                                                                readOnly={process?.send}
                                                                value={dataForm?.password || ''}
                                                                placeholder={labels?.newPassword?.[languageObj?.id]}
                                                                title={labels?.newPassword?.[languageObj?.id]}
                                                                type={showPassword ? 'text' : 'password'} />
                                                            <div className='input-group-append'>
                                                                <button className='btn btn-light bg-light border-0 text-muted' type='button' onClick={(e) => setShowPassword(!showPassword)}>
                                                                    <span className={showPassword ? icons?.fasFa?.eye : icons?.fasFa?.eyeSlash} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className='form-text text-danger mt-1' style={styles?.inputMsgAlert}>{parse(errors?.password)}</div>
                                                    </div>
                                                    <div className='form-group mb-4'>
                                                        <small className={settings?.html?.labels?.mutedUpperFontWeightBoldDblockMb2}>
                                                            <i className={`fas fa-check-double mr-1`} /> {labels?.confirmNewPassword?.[languageObj?.id]}
                                                        </small>
                                                        <div className='input-group shadow-sm' style={{ borderRadius: '5px', overflow: 'hidden' }}>
                                                            <input id={controls?.inputs?.passwordConfirm?.id}
                                                                className='form-control form-control-lg bg-light border-0 text-dark'
                                                                maxLength={dataFormLength?.max?.password}
                                                                onChange={(e) => setDataForm(prev => ({ ...prev, passwordConfirm: e?.target?.value }))}
                                                                onFocus={() => setErrors(prev => ({ ...prev, passwordConfirm: '' }))}
                                                                readOnly={process?.send}
                                                                value={dataForm?.passwordConfirm || ''}
                                                                placeholder={labels?.confirmNewPassword?.[languageObj?.id]}
                                                                title={labels?.confirmNewPassword?.[languageObj?.id]}
                                                                type={showPassword ? 'text' : 'password'} />
                                                            <div className='input-group-append'>
                                                                <button className='btn btn-light bg-light border-0 text-muted' type='button' onClick={(e) => setShowPassword(!showPassword)}>
                                                                    <span className={showPassword ? icons?.fasFa?.eye : icons?.fasFa?.eyeSlash} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                        <div className='form-text text-danger mt-1' style={styles?.inputMsgAlert}>{parse(errors?.passwordConfirm)}</div>
                                                    </div>

                                                </div>

                                                {/*Footer*/}
                                                <div className='card-footer py-4 bg-white border-top-0'>
                                                    <div className='d-flex justify-content-center w-100'>
                                                        <button id={controls?.buttons?.send?.id}
                                                            title={labels?.send?.[languageObj?.id]}
                                                            data-toggle='tooltip'
                                                            type='submit'
                                                            disabled={process?.send}
                                                            style={{ borderRadius: '5px' }}
                                                            className={controls?.buttons?.send?.defaultClass}>
                                                            {!process?.send ? (
                                                                <i id={controls?.icons?.send.id} className={`${icons?.shareSquare}`} />
                                                            ) : (
                                                                <span id={controls?.spinners?.send?.id} className={`${settings?.react?.html?.spinner?.borderSm}`} aria-hidden='true'></span>
                                                            )} {labels?.send?.[languageObj?.id]}
                                                        </button>
                                                        <a id={controls?.buttons?.back?.id}
                                                            href={process?.send ? '/#' : backend?.urls?.login}
                                                            title={labels?.back?.[languageObj?.id]}
                                                            style={{ borderRadius: '5px' }}
                                                            className={controls?.buttons?.back?.defaultClass}>
                                                            <i className={`${icons?.fasFa?.arrowAltCircleLeft}`} /> {labels?.back?.[languageObj?.id]}
                                                        </a>
                                                    </div>
                                                </div>
                                            </form>

                                            <div>
                                                <ReCAPTCHA className='g-recaptcha'
                                                    hl={languageObj?.id}
                                                    ref={reCaptchaRef}
                                                    sitekey={settings?.react?.reCAPTCHA?.reCAPTCHASiteKey}
                                                    size='invisible' />
                                            </div>
                                        </div>

                                    </div>

                                    <div className='text-muted text-center text-small'>
                                        <p>
                                            <small>
                                                {labels?.protectedByReCAPTCHA?.[languageObj?.id]} - <a target='_blank'
                                                    href={settings?.react?.reCAPTCHA?.urlPrivaciConditions}
                                                    rel='noreferrer'>{labels?.privacyAndConditions?.[languageObj?.id]}
                                                </a>
                                            </small>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div></div>
                        )}
                    </div>
                </section>

            </div>
        </div>
    )
}